import React from 'react';

/**
 * OSM-1983 — a small in-page message for a single chart, table or section.
 *
 * Distinct from `CustomerDataError`, which replaces a whole module page. Use
 * this when one part of an otherwise-working page has nothing to show, so the
 * rest of the page keeps rendering.
 *
 * It exists because these states were previously hand-typed per file as bare
 * `<div>Error fetching data</div>` (or, worse, `null` — which renders a blank
 * area with no explanation at all). A deleted BigQuery view is exactly the
 * case that hits this: the query 404s, and the user deserves to be told rather
 * than shown an empty rectangle.
 */
interface SectionMessageProps {
  /**
   * `error`  — the fetch failed; a retry might help.
   * `empty`  — the fetch worked, there is just nothing for this selection.
   */
  variant?: 'error' | 'empty';
  /** Optional override for the body copy. */
  message?: string;
  /** Optional retry handler; only meaningful for `error`. */
  onRetry?: () => void;
  className?: string;
}

const DEFAULT_COPY: Record<'error' | 'empty', string> = {
  error:
    "We couldn't load this section. Try again, or pick a different account " +
    'from the sidebar.',
  empty: 'No data available for the current selection.',
};

const SectionMessage: React.FC<SectionMessageProps> = ({
  variant = 'empty',
  message,
  onRetry,
  className = '',
}) => (
  <div
    className={`flex flex-col items-center justify-center py-10 px-4 text-center ${className}`}
    data-testid={`section-message-${variant}`}
    role={variant === 'error' ? 'alert' : undefined}
  >
    <p className="text-sm text-secondarygray max-w-sm">
      {message ?? DEFAULT_COPY[variant]}
    </p>
    {variant === 'error' && onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 text-xs font-bold py-1.5 px-4 rounded-full border border-thirdgray text-thirdgray hover:bg-gray50 transition-colors"
        data-testid="section-message-retry"
      >
        RETRY
      </button>
    )}
  </div>
);

export default SectionMessage;
